import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { AppDataTable } from '@root/features/app-data-table/app-data-table'
import { DataCounterButton } from '@root/shared/ui-kit'

import { reatomTradesTable } from '../atoms'

import { getTradeTableRowStyle } from './trades-table-util'

import type { TradesTableModel, TradesTableModelColumns } from '../atoms'
import type { PairAddress, PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'
import type { TableVirtuosoHandle } from 'react-virtuoso'

type TradeTableProps = {
  /**
   * Only needed when this builds its own model — a caller that brings one has
   * already decided what the table is of, which may be a wallet rather than a
   * pair.
   */
  pairAddress?: PairAddress
  pair: PairModel | undefined
  model?: TradesTableModel
  overrideModelColumns?: TradesTableModelColumns
  onlyUserWallets?: boolean
  compact?: boolean
} & ClassNameProps

export const TradesTable: React.FC<TradeTableProps> = ({
  className,
  pairAddress,
  pair,
  onlyUserWallets,
  model: model_,
  overrideModelColumns,
  compact = false,
}) => {
  const [hovered, setHovered] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const model = useMemo(
    () =>
      model_ ??
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomTradesTable({
        pairAddress,
        pair,
        wallets: onlyUserWallets ? 'user-wallets' : undefined,
      }),
    [model_, pairAddress, pair, onlyUserWallets],
  )
  const initialize = useAction(model.initialize)

  useEffect(() => {
    if (model_) {
      return
    }
    return initialize()
  }, [initialize, model_])

  const virtuosoRef = useRef<TableVirtuosoHandle>(null)
  const { t } = useTranslation()
  const [paused] = useAtom(model.paused)
  const [buffedCount] = useAtom(model.buffedCount)
  const setPaused = useAction(model.setPaused)

  const handleNewDataClick = () => {
    virtuosoRef.current?.scrollToIndex({
      index: 0,
      behavior: 'smooth',
    })
  }

  const floatingActions =
    paused && buffedCount > 0 ? (
      <DataCounterButton
        counter={buffedCount}
        text={t('trade.table.new-trades')}
        onClick={handleNewDataClick}
      />
    ) : undefined

  useEffect(() => {
    setPaused(hovered || scrolled)
  }, [hovered, scrolled, setPaused])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop
    const passedThreshold = scrollTop > 20
    setScrolled(passedThreshold)
  }

  const handleMouseEnter = () => {
    setHovered(true)
  }

  const handleMouseLeave = () => {
    setHovered(false)
  }

  useEffect(() => {
    setPaused(false)
  }, [setPaused])

  return (
    <Root>
      <AppDataTable
        css={css`
          height: 100%;
          ${compact
            ? css`
                --data-table-row-padding-inline: 0;
                --data-table-header-padding-inline: 0;
              `
            : undefined}
        `}
        overrideModelColumns={overrideModelColumns}
        virtuosoRef={virtuosoRef}
        floatingActions={floatingActions}
        className={className}
        model={model}
        rowHeight={compact ? 30 : 40}
        getRowStyle={getTradeTableRowStyle}
        onScroll={handleScroll}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        disableIndex={true}
      />
    </Root>
  )
}

const Root = styled.div`
  height: 100%;
  position: relative;
`
